import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BsX, BsHouseHeart, BsCheckCircleFill } from "react-icons/bs";
import { PrimaryContext } from "../MainContext/MainContext";

const DISMISS_KEY = "HOME_DELIVERY_POPUP_DISMISSED_AT";
const REMIND_AFTER_HOURS = 24; // re-show a day later if user dismissed without adding an address

/**
 * Announces the September 2026 switch to home delivery to signed-in users who
 * haven't added an address yet. Copy comes from i18n keys home_delivery_notice_*
 * so it stays translatable.
 */
const HomeDeliveryPopup = () => {
  const { t } = useTranslation();
  const { isAuthorized, user } = useContext(PrimaryContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isAuthorized || !user?.id) {
      setVisible(false);
      return;
    }
    if (location.pathname.includes("/personal-cabinet/my-addresses")) {
      setVisible(false);
      return;
    }
    if ((user.addresses_count ?? 0) > 0) {
      setVisible(false);
      return;
    }
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const hoursSince = (Date.now() - dismissedAt) / (1000 * 60 * 60);
    if (dismissedAt && hoursSince < REMIND_AFTER_HOURS) {
      setVisible(false);
      return;
    }
    setVisible(true);
  }, [isAuthorized, user?.id, user?.addresses_count, location.pathname]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const goToAddresses = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    navigate("/personal-cabinet/my-addresses");
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[1000] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-xl p-8 md:p-5 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-red-500"
          aria-label="close"
        >
          <BsX className="w-8 h-8" />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-teal_600 flex items-center justify-center mb-4">
            <BsHouseHeart className="w-8 h-8 text-white" />
          </div>

          <h2 className="text-xl md:text-lg font-bold mb-2">
            {t("home_delivery_notice_greeting") || "ძვირფასო მომხმარებელო,"}
          </h2>

          <p className="text-sm md:text-xs text-gray-700 mb-3 leading-relaxed">
            {t("home_delivery_notice_p1") ||
              "თქვენი სურვილებისა და მრავალრიცხოვანი მოთხოვნების გათვალისწინებით, MGL Parcel აუმჯობესებს მომსახურებას, რათა ამანათების მიღება კიდევ უფრო მარტივი და კომფორტული გახდეს."}
          </p>

          <p className="text-sm md:text-xs text-gray-800 mb-3 leading-relaxed font-semibold">
            {t("home_delivery_notice_p2") ||
              "2026 წლის 1 სექტემბრიდან თქვენი ამანათები ოფისის ნაცვლად პირდაპირ თქვენს მიერ მითითებულ მისამართზე იქნება მიწოდებული."}
          </p>

          <p className="text-sm md:text-xs text-gray-700 mb-4 leading-relaxed">
            {t("home_delivery_notice_p3") ||
              "გთხოვთ, წინასწარ დაამატოთ თქვენი მისამართი პირად კაბინეტში, რათა ამანათები შეუფერხებლად მიიღოთ."}
          </p>

          <div className="w-full bg-teal-50 border border-teal_600 rounded-2xl p-4 mb-4 flex items-start gap-3 text-left">
            <BsCheckCircleFill className="w-5 h-5 text-teal_600 flex-shrink-0 mt-0.5" />
            <p className="text-sm md:text-xs text-gray-800 leading-relaxed">
              {t("home_delivery_notice_bonus") ||
                "მომხმარებლების სურვილის გათვალისწინებით, 10 კგ-მდე ამანათების მიწოდება საქართველოს ნებისმიერ ქალაქში უკვე შედის ტრანსპორტირების ტარიფში და დამატებითი საფასური არ დაგეკისრებათ."}
            </p>
          </div>

          <p className="text-xs text-gray-500 mb-5 italic">
            {t("home_delivery_notice_thanks") ||
              "გმადლობთ, რომ ირჩევთ MGL Parcel-ს!"}
          </p>

          <div className="flex gap-3 w-full sm:flex-col">
            <button
              onClick={goToAddresses}
              className="flex-1 rounded-full bg-teal_600 text-white font-bold text-sm px-6 py-3 hover:opacity-90"
            >
              {t("add_my_address_now") || "მისამართის დამატება"}
            </button>
            <button
              onClick={dismiss}
              className="rounded-full bg-gray-100 text-gray-600 font-bold text-sm px-6 py-3 hover:bg-gray-200"
            >
              {t("remind_later") || "შემახსენე მოგვიანებით"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeDeliveryPopup;
